import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { apiGet } from '../config/apiClient';

export default function StoreListPage() {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState(null);
  const [countries, setCountries] = useState([]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const activeCountry = searchParams.get('country') || '';

  useEffect(() => {
    apiGet('/stores/countries')
      .then((data) => setCountries(data || []))
      .catch(() => setCountries([]));
  }, []);

  const fetchStores = useCallback(async (page, countrySlug) => {
    setLoading(true);
    setError('');
    try {
      let url = `/stores?page=${page}`;
      if (countrySlug) url += `&country=${encodeURIComponent(countrySlug)}`;
      const data = await apiGet(url);
      setStores(data.data || []);
      setPagination({
        current_page: data.current_page,
        last_page: data.last_page,
        from: data.from,
        to: data.to,
        total: data.total,
        next_page_url: data.next_page_url,
        prev_page_url: data.prev_page_url,
      });
    } catch (err) {
      console.error('Fetch stores error:', err);
      setError(err.data?.message || err.message || 'An error occurred while fetching supermarkets.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchStores(currentPage, activeCountry);
  }, [currentPage, activeCountry, fetchStores]);

  const handleCountryChange = (slug) => {
    const params = {};
    if (slug) params.country = slug;
    params.page = '1';
    setSearchParams(params);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && pagination && newPage <= pagination.last_page) {
      const params = { page: newPage.toString() };
      if (activeCountry) params.country = activeCountry;
      setSearchParams(params);
    }
  };
  
  // Function to generate a placeholder logo if actual logo_url is missing
  const getLogoUrl = (store) => {
      return store.logo_url || '/placeholder-product.svg';
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-12">
      <h1 className="text-3xl font-bold mb-6 text-gray-800 text-center">All Supermarkets</h1>

      {countries.length >= 1 && (
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          <button
            onClick={() => handleCountryChange('')}
            className={`px-4 py-2 text-sm font-medium rounded-full border transition-colors duration-150 ${
              !activeCountry
                ? 'bg-red-800 text-white border-red-800'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
            }`}
          >
            All Countries
          </button>
          {countries.map((c) => (
            <button
              key={c.id}
              onClick={() => handleCountryChange(c.slug)}
              className={`px-4 py-2 text-sm font-medium rounded-full border transition-colors duration-150 ${
                activeCountry === c.slug
                  ? 'bg-red-800 text-white border-red-800'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
      )}

      {loading && (
        <div className="text-center py-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-800 mx-auto"></div>
          <p className="text-gray-500 mt-3">Loading supermarkets...</p>
        </div>
      )}
      {error && (
        <div className="text-center py-10 bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg shadow-md max-w-md mx-auto">
          <h3 className="font-semibold text-lg mb-2">Oops! Something went wrong.</h3>
          <p>Error: {error}</p>
        </div>
      )}

      {!loading && !error && stores.length === 0 && (
        <div className="text-center py-10 bg-gray-50 border border-gray-200 p-6 rounded-lg shadow-md max-w-md mx-auto">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <h3 className="mt-2 text-lg font-medium text-gray-900">No Supermarkets Found</h3>
          <p className="mt-1 text-sm text-gray-500">There are currently no supermarkets listed. Please check back later.</p>
        </div>
      )}

      {!loading && !error && stores.length > 0 && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {stores.map((store) => (
              <div 
                key={store.id} 
                className="bg-white rounded-xl shadow-lg p-6 flex flex-col items-center text-center hover:shadow-2xl transition-shadow duration-200 ease-in-out"
              >
                <img 
                  src={getLogoUrl(store)}
                  alt={`${store.name} logo`}
                  className="h-20 object-contain mb-4"
                  style={{maxHeight: '80px'}} 
                />
                <h2 className="text-xl font-semibold text-gray-800 mb-1">{store.name}</h2>
                {store.branches && store.branches.length > 0 ? (
                  <div className="w-full mb-3">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                      {store.branches.length} {store.branches.length === 1 ? 'Location' : 'Locations'}
                    </p>
                    <ul className="space-y-1">
                      {store.branches.map((branch) => (
                        <li key={branch.id} className="text-xs text-gray-500 flex items-center justify-center gap-1">
                          <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span>{branch.name || branch.address || branch.neighborhood || 'Branch'}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <>
                    {store.city && <p className="text-sm text-gray-500 mb-1">{store.city}</p>}
                    {store.address && <p className="text-xs text-gray-400 mb-3">{store.address}</p>}
                  </>
                )}
                <Link 
                  to={`/stores/${store.slug || store.id}`}
                  className="mt-auto w-full bg-red-800 text-white text-sm px-4 py-2 rounded-md hover:bg-red-900 transition duration-150 ease-in-out"
                >
                  View Details / Products
                </Link>
              </div>
            ))}
          </div>

          {/* Enhanced Pagination Controls */}
          {pagination && pagination.last_page > 1 && (
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-10 pt-6 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                Showing <span className="font-semibold">{pagination.from || 0}</span> to <span className="font-semibold">{pagination.to || 0}</span> of <span className="font-semibold">{pagination.total || 0}</span> results
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={pagination.current_page === 1}
                  className="px-3 py-1.5 text-xs font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  First
                </button>
                <button
                  onClick={() => handlePageChange(pagination.current_page - 1)}
                  disabled={!pagination.prev_page_url}
                  className="px-3 py-1.5 text-xs font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  &laquo; Prev
                </button>
                <span className="text-sm text-gray-700">
                  Page <input 
                          type="number" 
                          value={pagination.current_page}
                          onChange={(e) => handlePageChange(parseInt(e.target.value,10))}
                          min="1"
                          max={pagination.last_page}
                          className="mx-1 px-2 py-1 w-12 text-center border border-gray-300 rounded-md"
                        /> of {pagination.last_page}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.current_page + 1)}
                  disabled={!pagination.next_page_url}
                  className="px-3 py-1.5 text-xs font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next &raquo;
                </button>
                <button
                  onClick={() => handlePageChange(pagination.last_page)}
                  disabled={pagination.current_page === pagination.last_page}
                  className="px-3 py-1.5 text-xs font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
